import { useEffect, useRef } from 'react';
import createGlobe from 'cobe';

import './styles.scss';

// [lat, long] of a spread of hubs. São Paulo anchors the network (Brazil-first
// platform) and threads out to the Americas, Europe, and a few global points.
const CITY = {
    saoPaulo: [-23.55, -46.63],
    rio: [-22.91, -43.17],
    buenosAires: [-34.6, -58.38],
    santiago: [-33.45, -70.67],
    bogota: [4.71, -74.07],
    mexicoCity: [19.43, -99.13],
    miami: [25.76, -80.19],
    newYork: [40.71, -74.01],
    toronto: [43.65, -79.38],
    lisbon: [38.72, -9.14],
    madrid: [40.42, -3.7],
    london: [51.51, -0.13],
    paris: [48.85, 2.35],
    berlin: [52.52, 13.4],
    dubai: [25.2, 55.27],
    capeTown: [-33.92, 18.42],
    tokyo: [35.68, 139.65],
    sydney: [-33.87, 151.21],
};

const MARKERS = [
    /*{ location: CITY.saoPaulo, size: 0.0169 },
    { location: CITY.rio, size: 0.0165 },
    { location: CITY.buenosAires, size: 0.0165 },
    { location: CITY.santiago, size: 0.0164 },
    { location: CITY.bogota, size: 0.0164 },
    { location: CITY.mexicoCity, size: 0.0165 },
    { location: CITY.miami, size: 0.0165 },
    { location: CITY.newYork, size: 0.0166 },
    { location: CITY.toronto, size: 0.0164 },
    { location: CITY.lisbon, size: 0.0165 },
    { location: CITY.madrid, size: 0.0165 },
    { location: CITY.london, size: 0.0166 },
    { location: CITY.paris, size: 0.0165 },
    { location: CITY.berlin, size: 0.0164 },
    { location: CITY.dubai, size: 0.0165 },
    { location: CITY.capeTown, size: 0.0164 },
    { location: CITY.tokyo, size: 0.0165 },
    { location: CITY.sydney, size: 0.0164 },*/
];

// Great-circle connections between the hubs — the "lines connecting countries".
const ARCS = [
    /*{ from: CITY.saoPaulo, to: CITY.lisbon },
    { from: CITY.saoPaulo, to: CITY.newYork },
    { from: CITY.saoPaulo, to: CITY.london },
    { from: CITY.saoPaulo, to: CITY.buenosAires },
    { from: CITY.saoPaulo, to: CITY.miami },
        { from: CITY.saoPaulo, to: CITY.berlin },
        { from: CITY.saoPaulo, to: CITY.dubai },

    { from: CITY.rio, to: CITY.madrid },
    { from: CITY.newYork, to: CITY.london },
    { from: CITY.lisbon, to: CITY.london },
    { from: CITY.miami, to: CITY.mexicoCity },
    { from: CITY.miami, to: CITY.bogota },
    { from: CITY.newYork, to: CITY.toronto },
    { from: CITY.london, to: CITY.berlin },
    { from: CITY.paris, to: CITY.dubai },
    { from: CITY.dubai, to: CITY.tokyo },
    { from: CITY.tokyo, to: CITY.sydney },
    { from: CITY.buenosAires, to: CITY.santiago },
    { from: CITY.capeTown, to: CITY.dubai },*/
];

// cobe needs raw RGB floats (0..1), not CSS vars, so the neutral surface scale is
// mirrored here per theme. The globe stays grayscale on purpose — the house accent
// is reserved for interaction, never decoration.
const THEME = {
    dark: {
        dark: 1,
        baseColor: [0.34, 0.35, 0.36],
        markerColor: [0.55, 0.57, 0.58],
        glowColor: [0.13, 0.14, 0.15],
        arcColor: [0.62, 0.64, 0.66],
        mapBrightness: 3.2,
        mapBaseBrightness: 0,
        diffuse: 3,
        opacity: 0.8,
    },
    light: {
        dark: 0,
        baseColor: [0.78, 0.8, 0.82],
        markerColor: [0.22, 0.24, 0.26],
        glowColor: [0.92, 0.94, 0.96],
       arcColor: [0.38, 0.4, 0.42],
        mapBrightness: 1.1,
        mapBaseBrightness: 0,
        diffuse: 1.99,
        opacity: 0.62,
    },
};

const ROTATION_SPEED = 0.0022; // rad/frame — slow and calm, per the motion guidelines
const THETA = 0.26; // fixed tilt so the northern hemisphere reads
const INITIAL_PHI = 4.7; // starts over the Atlantic; it auto-rotates from here anyway
const MAX_DPR = 1.5; // the canvas is huge (140vh square) — cap the buffer to stay light
const LERP = 0.08; // per-frame ease for the light/dark cross-fade

function currentAppearance() {
    if (typeof window !== 'undefined' && window.appearenceMode) {
        return window.appearenceMode === 'dark' ? 'dark' : 'light';
    }
    if (typeof document !== 'undefined' && document.body.classList.contains('dark')) {
        return 'dark';
    }
    return 'light';
}

export default function GlobeBackground({ className = '' }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        // Captured before createGlobe reparents the canvas (see cleanup).
        const frame = canvas.parentElement;

        // Re-evaluated live (see the 'change' listener below) so toggling the OS
        // "reduce motion" setting mid-session actually starts/stops the rotation.
        const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        let reduceMotion = motionQuery?.matches ?? false;
        const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);

        // `live` is what we render; `target` is where the current theme wants it. Every
        // frame nudges live → target so a theme flip cross-fades instead of snapping.
        const target = { ...THEME[currentAppearance()] };
        const live = {
            dark: target.dark,
            baseColor: [...target.baseColor],
            markerColor: [...target.markerColor],
            glowColor: [...target.glowColor],
            arcColor: [...target.arcColor],
            mapBrightness: target.mapBrightness,
            mapBaseBrightness: target.mapBaseBrightness,
            diffuse: target.diffuse,
            opacity: target.opacity,
        };

        let width = 0;
        let height = 0;
        const measure = () => {
            width = canvas.clientWidth;
            height = canvas.clientHeight;
        };
        measure();

        let phi = INITIAL_PHI;
        let raf = 0;
        let running = true;

        const globe = createGlobe(canvas, {
            devicePixelRatio: dpr,
            width,
            height,
            phi,
            theta: THETA,
            mapSamples: 9500,
            mapBrightness: live.mapBrightness,
            mapBaseBrightness: live.mapBaseBrightness,
            baseColor: live.baseColor,
            markerColor: live.markerColor,
            glowColor: live.glowColor,
            arcColor: live.arcColor,
            arcWidth: 0.2,
            arcHeight: 0.28,
            markerElevation: 0,
            markerSize: 0.015,
            diffuse: live.diffuse,
            dark: live.dark,
            opacity: live.opacity,
            markers: MARKERS,
            arcs: ARCS,
        });

        const lerp = (a, b) => a + (b - a) * LERP;
        const lerpInto = (arr, dest) => {
            for (let i = 0; i < arr.length; i += 1) arr[i] = lerp(arr[i], dest[i]);
        };

        function draw() {
            lerpInto(live.baseColor, target.baseColor);
            lerpInto(live.markerColor, target.markerColor);
            lerpInto(live.glowColor, target.glowColor);
            lerpInto(live.arcColor, target.arcColor);
            live.dark = lerp(live.dark, target.dark);
            live.mapBrightness = lerp(live.mapBrightness, target.mapBrightness);
            live.mapBaseBrightness = lerp(live.mapBaseBrightness, target.mapBaseBrightness);
            live.diffuse = lerp(live.diffuse, target.diffuse);
            live.opacity = lerp(live.opacity, target.opacity);

            if (!reduceMotion) phi += ROTATION_SPEED;

            // markers/arcs are omitted here on purpose — passing them re-uploads their
            // buffers every frame; they were set once at creation and never change.
            globe.update({
                phi,
                theta: THETA,
                dark: live.dark,
                baseColor: live.baseColor,
                markerColor: live.markerColor,
                glowColor: live.glowColor,
                arcColor: live.arcColor,
                mapBrightness: live.mapBrightness,
                mapBaseBrightness: live.mapBaseBrightness,
                diffuse: live.diffuse,
                opacity: live.opacity,
            });

            // With motion reduced there's nothing to animate once the theme has settled,
            // so let the loop idle instead of redrawing an unchanging frame forever.
            const settled =
                Math.abs(live.dark - target.dark) < 0.001 &&
                Math.abs(live.opacity - target.opacity) < 0.001 &&
                Math.abs(live.mapBrightness - target.mapBrightness) < 0.01;
            if (reduceMotion && settled) {
                running = false;
                return;
            }
            raf = requestAnimationFrame(draw);
        }

        const wake = () => {
            if (running) return;
            running = true;
            raf = requestAnimationFrame(draw);
        };

        const onAppearance = () => {
            Object.assign(target, THEME[currentAppearance()]);
            wake();
        };
        window.addEventListener('appearence-change', onAppearance);

        // Honor a live change to the reduce-motion preference: wake() resumes the loop
        // (which restarts rotation, or lets the settle branch park it when reduced).
        const onMotionPref = (e) => {
            reduceMotion = e.matches;
            wake();
        };
        motionQuery?.addEventListener('change', onMotionPref);

        // Re-measure on viewport changes (vh is viewport-relative). cobe only resizes
        // its buffer when both dimensions are passed, so keep them out of the frame loop.
        const ro = new ResizeObserver(() => {
            measure();
            if (width && height) {
                globe.update({ width, height });
                wake();
            }
        });
        ro.observe(canvas);

        raf = requestAnimationFrame(draw);

        return () => {
            running = false;
            cancelAnimationFrame(raf);
            ro.disconnect();
            window.removeEventListener('appearence-change', onAppearance);
            motionQuery?.removeEventListener('change', onMotionPref);
            globe.destroy();
            // cobe wraps the canvas in a helper <div> (for its CSS-anchor overlays). Put
            // the canvas back where React expects it so a remount doesn't nest wrappers.
            const wrapper = canvas.parentElement;
            if (frame && wrapper && wrapper !== frame) {
                frame.appendChild(canvas);
                wrapper.remove();
            }
        };
    }, []);

    return (
        <div className={`insights-globe${className ? ` ${className}` : ''}`} aria-hidden="true">
            <canvas ref={canvasRef} className="insights-globe__canvas" />
        </div>
    );
}
